import {apiClient} from 'core/apiClient';
import {formatDate} from 'utils/date';

import {TracingDetailRaw, TracingGridRowRaw, TracingNode} from '../types';
import {ApiFilters, ApiPaging, ApiSortings, SimplePaginationContent} from '../types/api';

let id = 0;
let gridTokensCache: Record<number, string | null> = {
    0: null,
};

const GRAPH_URL = '/service/graph';

export function clearTracingGridCache() {
    gridTokensCache = {
        0: null,
    };
}

export async function fetchTracingGrid(params: {
    sortings: ApiSortings;
    filters: ApiFilters;
    paging: ApiPaging;
    search: string;
    period: [Date | null, Date | null];
    tin: string;
}): Promise<SimplePaginationContent<TracingGridRowRaw>> {
    const period = {
        from: params.period[0] ? formatDate(params.period[0], 'yyyy-MM-dd') : '',
        to: params.period[1] ? formatDate(params.period[1], 'yyyy-MM-dd') : '',
    };

    const requestParams = {
        sortings: params.sortings,
        filters: params.filters.map((f) => ({value: f.value, field: f.id})),
        paging: params.paging,
        search: params.search || '',
        period,
        ...(params.tin && {tin: params.tin}),
    };

    const response = await apiClient
        .post(GRAPH_URL + '/tracing-grid', requestParams, {baseURL: '/api/v2'})
        .then((res) => res.data);

    return {content: response.content, hasNext: response.hasNext};
}

export async function fetchTracingDetail(params: {
    id: string;
    gtin: string;
    period: [Date | null, Date | null];
}): Promise<TracingDetailRaw | null> {
    const period = {
        from: params.period[0] ? formatDate(params.period[0], 'yyyy-MM-dd') : '',
        to: params.period[1] ? formatDate(params.period[1], 'yyyy-MM-dd') : '',
    };

    const product = await apiClient
        .get<TracingDetailRaw>(GRAPH_URL + '/tracing-detail', {
            params: {id: +params.id, gtin: params.gtin, ...period},
            baseURL: '/api/v2',
        })
        .then((res) => res.data);
    return product ?? null;
}

export async function fetchTracingNodeInfo(params: {
    id?: string;
    gtin?: string;
    parentId?: string;
    period: [Date | null, Date | null];
}) {
    if (!params.id || !params.gtin) throw new Error('No data error');

    const requestParams = {
        id: +params.id,
        gtin: params.gtin,
        parentId: params.parentId ? +params.parentId : null,
        from: params.period[0] ? formatDate(params.period[0], 'yyyy-MM-dd') : '',
        to: params.period[1] ? formatDate(params.period[1], 'yyyy-MM-dd') : '',
    };

    const response = await apiClient
        .get(GRAPH_URL + '/tracing-node-info', {params: requestParams, baseURL: '/api/v2'})
        .then((res) => res.data);

    return response as {
        childrenCount: string;
        avgPrice: string;
        quantity: string;
        soldQuantity: string;
        dropoutQuantity: string;
        inputQuantity: string;
        startAmount: string;
    };
}

export async function fetchTracingGraph(params: {
    id?: string;
    gtin?: string;
    period: [Date | null, Date | null];
    producedQuantity?: number;
    startQuantity?: number;
}): Promise<TracingNode | null> {
    if (!params.id || !params.gtin) throw new Error('No data error');

    const requestParams = {
        id: +params.id,
        gtin: params.gtin,
        from: params.period[0] ? formatDate(params.period[0], 'yyyy-MM-dd') : '',
        to: params.period[1] ? formatDate(params.period[1], 'yyyy-MM-dd') : '',
    };

    const root = await apiClient
        .get<TracingNode>(GRAPH_URL + '/tracing-graph', {
            baseURL: '/api/v2',
            params: requestParams,
        })
        .then((res) => res.data);

    if (!root) return null;
    root.gtin = params.gtin;
    root.localId = (id++).toString();
    root.childrenCount = +root.childrenCount;
    root.name = root.name || 'No data';
    const children = root.children ?? [];
    children.forEach((child) => {
        child.localId = (id++).toString();
        child.gtin = root.gtin;
    });

    const parents = root.parents ?? [];
    parents.forEach((parent) => {
        parent.localId = (id++).toString();
        parent.gtin = root.gtin;
    });

    const tracePromiseGenerator = (list: TracingNode[]) =>
        Promise.all(
            list.map((item) =>
                fetchTracingNodeInfo({
                    id: item.id,
                    gtin: item.gtin,
                    period: params.period,
                    parentId: root.id,
                }).then(
                    ({
                        childrenCount,
                        soldQuantity,
                        startAmount,
                        avgPrice,
                        dropoutQuantity,
                        inputQuantity,
                        quantity,
                    }) =>
                        ({
                            ...item,
                            startAmount: +startAmount,
                            childrenCount: +childrenCount,
                            soldAmount: +soldQuantity,
                            dropoutAmount: +dropoutQuantity,
                            inputAmount: +inputQuantity,
                            outputAmount: +quantity,
                            sum: +avgPrice,
                        } as TracingNode),
                ),
            ),
        );

    const [filledRoot, formattedChildren, formattedParent] = await Promise.all([
        fetchTracingNodeInfo({
            id: root.id,
            gtin: root.gtin,
            period: params.period,
        }).then(
            ({
                childrenCount,
                avgPrice,
                quantity,
                soldQuantity,
                inputQuantity,
                startAmount,
                dropoutQuantity,
            }) => ({
                ...root,
                childrenCount: +childrenCount,
                inputAmount: params?.producedQuantity ? params.producedQuantity : +inputQuantity,
                startAmount: params?.startQuantity ? params.startQuantity : +startAmount,
                outputAmount: +quantity,
                dropoutAmount: +dropoutQuantity,
                sum: +avgPrice,
                soldAmount: +soldQuantity,
            }),
        ),
        tracePromiseGenerator(children),
        tracePromiseGenerator(parents),
    ]);
    return {
        ...filledRoot,
        children: formattedChildren,
        parents: formattedParent,
    };
}
